import { useLayoutEffect, useState } from 'react';

import { useNavigate } from 'react-router';

import { Alert, Box, Tab, Tabs } from '@mui/material';

import { Responsive } from 'components/content';
import { Block, OneColumn } from 'components/page';
import { useLoaderContext } from 'context/LoaderContext';
import { usePermissionsContext } from 'context/PermissionsContext';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useQueryBi } from 'pages/bi/hooks';
import { ConfigBi } from 'pages/bi/types';

export function BiPageComp() {
    const { hasPermission } = usePermissionsContext();
    const navigate = useNavigate();
    const { isDownMd } = useBreakpoints();
    const { isSuccess: isSuccessBi, isError, isFetching: isFetchingBi, data = [] } = useQueryBi();
    const [activeBi, setActiveBi] = useState<ConfigBi | null>(null);
    const [permissionedBis, setPermissionedBis] = useState<ConfigBi[]>([]);
    const altura = isDownMd && activeBi?.dsUrlMobile && activeBi?.vlAlturaMobile ? activeBi.vlAlturaMobile : activeBi?.vlAltura;
    const url = isDownMd && activeBi?.dsUrlMobile ? activeBi.dsUrlMobile : activeBi?.dsUrl;

    /**
     * Ao carregar as permissões e BI's, define o BI ativo como o BI de início ou o primeiro da lista.
     * Caso não haja BI's disponíveis e o usuário tenha permissão para visualizar o comunicado,
     * redireciona para a página de comunicado.
     */
    useLayoutEffect(() => {
        if (isSuccessBi) {
            setPermissionedBis(data);

            setActiveBi(data.find((item) => item.idConfigBi === 1) || data[0] || null);

            if (!data.length && hasPermission(['COMUNICADO_VISUALIZAR'])) {
                navigate('/comunicado');
            }
        }
    }, [isSuccessBi]);

    /**
     * Se está carregando os BI's, exibe o loading global.
     */
    useLoaderContext(isFetchingBi);

    if (isFetchingBi) return null;

    /**
     * Caso tenha falha na requisição dos BI's, exibe alerta de falha.
     */
    if (isError)
        return (
            <Alert sx={{ m: 2 }} severity="error">
                Falha ao carregar painéis de BI.
            </Alert>
        );

    /**
     * Caso a request não retorne nenhum BI, exibe alerta de panel não configurado.
     */
    if (!data.length)
        return (
            <Alert sx={{ m: 2 }} severity="warning">
                Painel BI não configurado.
            </Alert>
        );

    /**
     * Se não há BI ativo, o usuário não tem permissão para acessar nenhum BI.
     * Permanece na tela vazia ou redireciona para a página de comunicado.
     */
    if (!activeBi) return null;

    return (
        <OneColumn
            maxWidth={false}
            title="Indicadores"
            extraButtons={[
                {
                    title: 'Configurar Painéis de BI',
                    onClick: () => navigate('complementar/configuracao-bi'),
                    visible: hasPermission(['COMPLEMENTAR_CONFIG_BI']),
                },
            ]}
        >
            <Block sx={{ paddingTop: 1, gap: 2, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        allowScrollButtonsMobile
                        variant="scrollable"
                        scrollButtons="auto"
                        value={activeBi.idConfigBi}
                        sx={{ '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 } }}
                        onChange={(_, value) => setActiveBi(permissionedBis.find((item) => item.idConfigBi === value) as ConfigBi)}
                    >
                        {permissionedBis.map((item) => (
                            <Tab key={item.idConfigBi} label={item.dsConfigBi} value={item.idConfigBi} />
                        ))}
                    </Tabs>
                </Box>

                <Responsive height={altura}>
                    <iframe title={activeBi.dsConfigBi} id={`iframe_bi_${activeBi.idConfigBi}`} src={url}></iframe>
                </Responsive>
            </Block>
        </OneColumn>
    );
}
